import RA from "@/components/forms/Radio.module.css";
import R from "@/components/forms/Radiogroup.module.css";

import { deleteFloatingIpMutation } from "@/admin/floating_ips/floatingIpsQuery.ts";
import { deleteVmMutation } from "@/admin/vm/vmQuery.ts";
import type { GQVmItem } from "@/admin/vm/VmsTable.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { VmDeleteMessage } from "@/modules/compute/VmDeleteModal.tsx";
import { notNull } from "@warrenio/utils/notNull";
import { type ReactNode, useState } from "react";
import { Radio, RadioGroup } from "react-aria-components";

interface DeleteVmBlockModalProps {
    item: GQVmItem;
}

export function DeleteVmBlockModalContent({ item }: DeleteVmBlockModalProps) {
    const deleteIpMutation = useStandardMutation(deleteFloatingIpMutation);
    const deleteMutation = useStandardMutation(deleteVmMutation);
    const [deleteFloating, setDeleteFloating] = useState<string>("");

    const { uuid, name, ip_address_object, _location } = item;
    const attachedPublicIp = ip_address_object?.address;

    async function onSubmit() {
        await deleteMutation.mutateAsync({ uuid, location: notNull(_location) });
        if (deleteFloating) {
            await deleteIpMutation.mutateAsync({ location: notNull(_location), address: notNull(attachedPublicIp) });
        }
    }

    return (
        <WModalContent
            title="Delete Virtual Machine"
            label="Delete"
            autoFocus
            isActionDisabled={!!attachedPublicIp && deleteFloating === ""}
            modalAction={async () => await onSubmit()}
        >
            <VmDeleteMessage name={name} />

            {!!attachedPublicIp && (
                <>
                    <p className="pb-3">
                        There is floating IP <b>{attachedPublicIp}</b> assigned to this resource.
                    </p>

                    <p className="font-size-subtitle pb-2">What would you like to do?</p>
                    <RadioGroup
                        className={R.RadioGroup}
                        aria-label="Keep or delete floating IP"
                        value={deleteFloating}
                        orientation="horizontal"
                        onChange={setDeleteFloating}
                    >
                        <Radio className={RA.Radio} value="no">
                            Keep floating IP
                        </Radio>
                        <Radio className={RA.Radio} value="yes">
                            Delete floating IP
                        </Radio>
                    </RadioGroup>
                </>
            )}
        </WModalContent>
    );
}

interface DeleteVmBlockProps extends DeleteVmBlockModalProps {
    button: ReactNode;
}

export function DeleteVmBlock(props: DeleteVmBlockProps) {
    return (
        <WModal button={props.button}>
            <DeleteVmBlockModalContent {...props} />
        </WModal>
    );
}
